/**
 * CountDoc — printable "Count voucher" document that pairs with the
 * denomination grid on `/cash/petty-cash/count`.
 *
 * Mirrors `prototype/cash/petty-cash-count.html ▸ .doc-paper`: drawer
 * letterhead + counted-by/witness meta grid + denomination items
 * table + totals (book / counted / variance). Variance is computed
 * here from `counts` vs `bookBalancePaise` so the document is the
 * source of truth — the same row that gets posted as a
 * `cash_count_adjustment` cash-movement on submit.
 *
 * Pure presentation. The route owns the counts state (typically via
 * `<DenominationInput>` controlled with the same `counts` value).
 *
 * @see prototype/cash/petty-cash-count.html `.doc-paper`
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.4
 */
import { Wallet } from "lucide-react";
import { cn } from "@flint/ui";
import {
  DENOMINATIONS,
  totalPaise,
  totalPieces,
  type DenominationCounts,
} from "./denomination-input.tsx";
import { formatDate } from "../../../lib/format.ts";
import { formatVariance } from "../lib/format.ts";

export interface CountDocProps {
  /** Voucher number — `PCV-COUNT-YYYY-MM-DD` style, assigned by the server. */
  voucherNumber: string;
  drawerName: string;
  /** Display name of the actor running the count. */
  countedBy: string;
  /** Display name of the witness (founder or senior employee, per ADR-0012 §10.4). */
  witness: string | null;
  /** Counted-on ISO date. */
  countDate: string;
  /** Current pieces-per-denom snapshot from `<DenominationInput>`. */
  counts: DenominationCounts;
  /** Book balance at count time, paise. */
  bookBalancePaise: string;
}

export function CountDoc({
  voucherNumber,
  drawerName,
  countedBy,
  witness,
  countDate,
  counts,
  bookBalancePaise,
}: CountDocProps) {
  const counted = totalPaise(counts);
  const book = BigInt(bookBalancePaise || "0");
  // Variance follows the prototype convention: counted − book. Positive =
  // over (more cash in drawer than book), negative = short. `formatVariance`
  // surfaces the human label + outcome tone.
  const variancePaise = counted - book;
  const variance = formatVariance(variancePaise);

  return (
    <article className="bg-paper border border-line rounded-md shadow-sm overflow-hidden">
      {/* Letterhead */}
      <header className="grid gap-4 md:grid-cols-[1fr_auto] p-6 border-b border-line bg-raised">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-grid place-items-center w-10 h-10 rounded-sm bg-sky-50 text-sky-700 border border-sky-100 shrink-0">
              <Wallet size={20} strokeWidth={2} aria-hidden />
            </span>
            <h2 className="text-lg font-bold tracking-tight">{drawerName}</h2>
          </div>
          <p className="text-xs text-muted leading-relaxed">
            <strong className="text-ink-2 font-semibold">Petty cash drawer</strong>
            {countedBy ? <> · Operated by {countedBy}</> : null}
            <br />
            Drawer convention: ADR-0012 §10 (Imprest System amendment)
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1">
            Count Voucher
          </p>
          <p className="text-sm text-ink-2 font-semibold mb-1">
            <span className="text-muted text-xs uppercase tracking-wider mr-1.5">Voucher #</span>
            <span className="num">{voucherNumber}</span>
          </p>
          <p className="text-xs text-muted">Cadence · Monthly</p>
        </div>
      </header>

      {/* Counted-by + date meta */}
      <div className="grid gap-4 md:grid-cols-2 p-6 border-b border-line">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1">
            Counted by
          </p>
          <p className="text-sm font-semibold text-ink">{countedBy}</p>
          <p className="text-xs text-muted mt-1 leading-relaxed">
            Authorised petty-cash operator under <strong>ADR-0012 §10.2</strong>.
            {witness ? <> Witnessed by {witness} per §10.4 invariants.</> : null}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <MetaCell label="Count date" value={formatDate(countDate)} sub="end-of-period count" />
          <MetaCell label="Witnessed by" value={witness ?? "—"} sub="Founder · ADR-0012 §10.4" />
        </div>
      </div>

      {/* Denomination items */}
      <table className="w-full text-sm">
        <thead className="bg-raised border-b border-line text-xs uppercase tracking-wider text-muted">
          <tr>
            <th className="text-left px-6 py-2 font-semibold">Denom</th>
            <th className="text-right px-6 py-2 font-semibold">Pieces</th>
            <th className="text-right px-6 py-2 font-semibold">Value</th>
            <th className="text-right px-6 py-2 font-semibold">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {DENOMINATIONS.map((denom) => {
            const pieces = counts[denom] || 0;
            const subPaise = BigInt(denom) * BigInt(pieces);
            return (
              <tr key={denom} className="border-b border-line last:border-b-0">
                <td className="px-6 py-2 text-ink-2">₹{denom / 100}</td>
                <td className="px-6 py-2 text-right num">{pieces}</td>
                <td className="px-6 py-2 text-right num text-muted">
                  {(denom / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
                <td className="px-6 py-2 text-right num font-medium">
                  {(Number(subPaise) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-raised">
          <tr className="text-sm font-semibold">
            <td className="px-6 py-2.5 text-left text-muted uppercase tracking-wider text-xs">
              Subtotal
            </td>
            <td className="px-6 py-2.5 text-right num">{totalPieces(counts)}</td>
            <td />
            <td className="px-6 py-2.5 text-right num text-ink">
              {(Number(counted) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Totals + variance */}
      <div className="grid gap-6 md:grid-cols-[1fr_auto] p-6 border-t border-line bg-bg">
        <p className="text-xs text-muted leading-relaxed max-w-prose">
          Standard <strong>RBI denominations</strong>. Operator must witness count with the founder
          or one other senior employee per <strong>ADR-0012 §10.4</strong>.
        </p>
        <dl className="text-sm md:min-w-[260px]">
          <div className="flex items-baseline justify-between gap-6 py-1">
            <dt className="text-muted">Book balance</dt>
            <dd className="num font-medium">
              {(Number(book) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-6 py-1">
            <dt className="text-muted">Counted total</dt>
            <dd className="num font-medium">
              {(Number(counted) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </dd>
          </div>
          <div
            className={cn(
              "flex items-baseline justify-between gap-6 py-2 mt-1 px-3 -mx-3 rounded-sm border",
              variance.outcome === "exact" && "bg-ok-50 text-ok border-ok-100",
              variance.outcome === "over" && "bg-sky-50 text-sky-700 border-sky-100",
              variance.outcome === "short" && "bg-warn-50 text-warn border-warn-100",
            )}
          >
            <dt className="font-semibold">Variance</dt>
            <dd className="num font-bold capitalize">{variance.text}</dd>
          </div>
        </dl>
      </div>
    </article>
  );
}

function MetaCell({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-paper border border-line rounded-sm px-3 py-2">
      <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-ink truncate">{value}</p>
      <p className="text-xs text-muted mt-0.5 truncate">{sub}</p>
    </div>
  );
}
